import numeral from "numeral";
import { includes, keys } from "./lodash";
import "numeral/locales";
import { __getStore__ } from "../redux/store";
import { Piping } from "../piping";
import { isNullOrEmpty } from ".";
import { InterviewState } from "../models/states/InterviewState";

const PRECISION = 15;

if (!numeral.locales.id) {
    numeral.register("locale", "id", {
        delimiters: {
            thousands: ".",
            decimal: ",",
        },
        abbreviations: {
            thousand: "ribu",
            million: "juta",
            billion: "milyar",
            trillion: "triliun",
        },
        ordinal: () => ".",
        currency: {
            symbol: "Rp",
        },
    });
}

if (!numeral.locales.ur) {
    numeral.register("locale", "ur", {
        delimiters: {
            thousands: " ",
            decimal: ",",
        },
        abbreviations: {
            thousand: "ہزار",
            million: "دس لاکھ",
            billion: "ارب",
            trillion: "ٹریلین",
        },
        ordinal: () => ".",
        currency: {
            symbol: "Rs",
        },
    });
}

if (!numeral.locales.ar) {
    numeral.register("locale", "ar", {
        delimiters: {
            thousands: " ",
            decimal: ",",
        },
        abbreviations: {
            thousand: "k",
            million: "m",
            billion: "b",
            trillion: "t",
        },
        ordinal: () => ".",
        currency: {
            symbol: "د.إ",
        },
    });
}

if (!numeral.locales.pt) {
    numeral.register("locale", "pt", {
        delimiters: {
            thousands: " ",
            decimal: ",",
        },
        abbreviations: {
            thousand: "k",
            million: "m",
            billion: "b",
            trillion: "t",
        },
        ordinal: () => "º",
        currency: {
            symbol: "€",
        },
    });
}

// chinese
if (!numeral.locales.zh) {
    numeral.register("locale", "zh", {
        delimiters: {
            thousands: ",",
            decimal: ".",
        },
        abbreviations: {
            thousand: "k",
            million: "m",
            billion: "b",
            trillion: "t",
        },
        ordinal: () => "º",
        currency: {
            symbol: "¤",
        },
    });
}

// hindi
if (!numeral.locales.hi) {
    numeral.register("locale", "hi", {
        delimiters: {
            thousands: ",",
            decimal: ".",
        },
        abbreviations: {
            thousand: "k",
            million: "m",
            billion: "b",
            trillion: "t",
        },
        ordinal: () => "º",
        currency: {
            symbol: "¤",
        },
    });
}

// grec
if (!numeral.locales.el) {
    numeral.register("locale", "el", {
        delimiters: {
            thousands: " ",
            decimal: ",",
        },
        abbreviations: {
            thousand: "k",
            million: "m",
            billion: "b",
            trillion: "t",
        },
        ordinal: () => "º",
        currency: {
            symbol: "¤",
        },
    });
}

// roumain
if (!numeral.locales.ro) {
    numeral.register("locale", "ro", {
        delimiters: {
            thousands: " ",
            decimal: ",",
        },
        abbreviations: {
            thousand: "k",
            million: "m",
            billion: "b",
            trillion: "t",
        },
        ordinal: () => "º",
        currency: {
            symbol: "¤",
        },
    });
}

// dutch
if (!numeral.locales.nl) {
    numeral.register("locale", "nl", {
        delimiters: {
            thousands: ".",
            decimal: ",",
        },
        abbreviations: {
            thousand: "k",
            million: "m",
            billion: "b",
            trillion: "t",
        },
        ordinal: () => "º",
        currency: {
            symbol: "€",
        },
    });
}

const hasLocale = (language: string) => includes(keys(numeral.locales), language);

export const setLocale = (language: string) => {
    numeral.locale(hasLocale(language) ? language : "en");
};

export const getNumeral = (value: string | number, lang?: string) => {
    setLocale(lang);
    return numeral(value);
};

export const isExponential = (value: string | number) => String(value).includes("e");

export const getFixedValue = (value: string | number, decimals: number) => {
    if (value === "") return value;
    const num = Number(value);
    if (!Number.isNaN(num)) return num.toFixed(decimals);
    return value;
};

export const decimalSeparator = (language: string) =>
    numeral.locales[language]?.delimiters.decimal || ".";

export const formatNumeric = (
    value: string | number,
    decimals: number,
    language: string,
    cultureInvariant?: boolean,
    autoFormatting?: boolean,
): string => {
    setLocale(language);
    if (value == null || value === "" || isExponential(value)) return "";
    if (cultureInvariant) return String(getFixedValue(value, decimals));
    if (autoFormatting) {
        let format = "0,0";
        if (decimals > 0) format += `.${Array(decimals + 1).join("0")}`;
        const numeralValue = getNumeral(value, language);
        const formatted = numeralValue.format(format);
        if (formatted !== "NaN") return formatted;
        return numeralValue.value().toString();
    }
    if (decimalSeparator(numeral.locale()) === ",") return String(value).replace(".", ",");
    return String(value);
};

export const round = (v: number) => {
    if (isExponential(v)) return v;
    return Number(v.toPrecision(PRECISION));
};

export const getNumberOfDecimal = (nb: string) => {
    const match = nb.match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
    if (!match) {
        return 0;
    }
    return Math.max(0, (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0));
};

export const recallNumber = (value: string, interview: InterviewState): number => {
    if (isNullOrEmpty(value)) return null;
    const p = Piping.evaluateRecalls(interview, value);
    return getNumeral(p).value();
};
